import { useState, useEffect, useCallback, useMemo } from "react";
import { useTeamRole } from "@/hooks/useTeamRole";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, UserX, Eye, UserPlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import InviteTeamModal from "@/components/InviteTeamModal";
import UpgradePrompt from "@/components/UpgradePrompt";
import LeadAssignmentModal from "@/components/LeadAssignmentModal";
import BulkReassignModal from "@/components/BulkReassignModal";
import StaffActivityModal from "@/components/StaffActivityModal";
import type { TeamMember } from "@/hooks/useTeamMembers";
import type { DbLead } from "@/lib/types";

interface Member {
  id: string;
  owner_name: string | null;
  team_role: string;
  invite_accepted: boolean;
  last_login: string | null;
  agency_email?: string | null;
}

function relativeTime(date: string | null): { text: string; stale: boolean } {
  if (!date) return { text: "Never", stale: true };
  const diff = Date.now() - new Date(date).getTime();
  const days = Math.floor(diff / 86_400_000);
  const stale = days > 7;
  if (days === 0) return { text: "today", stale: false };
  if (days === 1) return { text: "yesterday", stale: false };
  if (days < 7) return { text: `${days}d ago`, stale: false };
  return { text: `${days}d ago`, stale };
}

export default function TeamManagement() {
  const { agency, agencyId, refresh } = useTeamRole();
  const { user } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [allLeads, setAllLeads] = useState<DbLead[]>([]);
  const [assignTarget, setAssignTarget] = useState<Member | null>(null);
  const [reassignTarget, setReassignTarget] = useState<Member | null>(null);
  const [activityTarget, setActivityTarget] = useState<Member | null>(null);
  const [roleChangeTarget, setRoleChangeTarget] = useState<{ member: Member; newRole: "manager" | "staff" } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);

  const loadLeads = useCallback(async () => {
    if (!agencyId) return;
    const { data } = await supabase.from("leads").select("*");
    setAllLeads((data ?? []) as DbLead[]);
  }, [agencyId]);

  const loadMembers = useCallback(async () => {
    if (!agencyId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, owner_name, team_role, invite_accepted, last_login, agency_email")
        .eq("agency_id", agencyId);
      if (error) throw error;
      setMembers((data ?? []) as Member[]);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => { loadMembers(); loadLeads(); }, [loadMembers, loadLeads]);

  const unassignedLeads = useMemo(() => allLeads.filter(l => !(l as any).assigned_to), [allLeads]);
  const leadsForReassign = useMemo(
    () => reassignTarget ? allLeads.filter(l => (l as any).assigned_to === reassignTarget.id).map(l => l.id) : [],
    [allLeads, reassignTarget],
  );

  function leadCountFor(memberId: string) {
    return allLeads.filter(l => (l as any).assigned_to === memberId).length;
  }

  const seatLimits = agency?.seat_limits ?? { owner: 1, manager: 2, staff: 7 };
  const counts = {
    owner: members.filter(m => m.team_role === "owner").length,
    manager: members.filter(m => m.team_role === "manager").length,
    staff: members.filter(m => m.team_role === "staff").length,
  };

  function pct(used: number, limit: number) { return limit > 0 ? Math.min(100, (used / limit) * 100) : 0; }
  function barColor(used: number, limit: number) {
    const p = pct(used, limit);
    if (p >= 90) return "bg-hot";
    if (p >= 70) return "bg-warning";
    return "bg-success";
  }

  async function confirmChangeRole() {
    if (!roleChangeTarget) return;
    const { member, newRole } = roleChangeTarget;
    setRoleChangeTarget(null);
    try {
      const { data, error } = await (supabase as any).rpc("change_team_role", {
        _member_id: member.id,
        _new_role: newRole,
      });
      if (error || !data?.ok) throw new Error(error?.message ?? data?.error ?? "Failed to update role");
      toast.success("Role updated");
      loadMembers();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update role");
    }
  }

  function removeMember(member: Member) {
    const name = member.owner_name ?? "this member";
    const leadsCount = leadCountFor(member.id);
    if (leadsCount > 0) {
      toast.info(`${name} has ${leadsCount} assigned lead${leadsCount > 1 ? "s" : ""}. Reassign them first.`);
      setReassignTarget(member);
      return;
    }
    setRemoveTarget(member);
  }

  async function confirmRemoveMember() {
    if (!removeTarget) return;
    const name = removeTarget.owner_name ?? "this member";
    const memberId = removeTarget.id;
    setRemoveTarget(null);
    try {
      const { data, error } = await (supabase as any).rpc("remove_team_member", { _member_id: memberId });
      if (error || !data?.ok) throw new Error(error?.message ?? data?.error ?? "Failed to remove member");
      toast.success(`${name} removed from team`);
      loadMembers();
    } catch (err: any) {
      toast.error(err.message ?? "Failed to remove member");
    }
  }

  const seatTiles = [
    { label: "Owners", used: counts.owner, limit: seatLimits.owner, fixed: true },
    { label: "Managers", used: counts.manager, limit: seatLimits.manager, fixed: false },
    { label: "Staff", used: counts.staff, limit: seatLimits.staff, fixed: false },
  ];

  const anyAtLimit = !seatTiles[1].fixed && counts.manager >= seatLimits.manager
    || counts.staff >= seatLimits.staff;

  return (
    <div className="space-y-6">
      {/* Seat Usage Banner */}
      <div className="rounded-xl bg-card border-l-4 border-gold border-y border-r p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-navy">Your Team — {agency?.name ?? "My Agency"}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Manage team members and seat usage.</p>
          </div>
          <Button
            onClick={() => setInviteOpen(true)}
            className="bg-gold text-gold-foreground hover:bg-gold/90 font-bold gap-1.5"
          >
            <Plus className="h-4 w-4" /> Invite Team Member
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {seatTiles.map(tile => {
            const atLimit = !tile.fixed && tile.used >= tile.limit;
            return (
              <div key={tile.label} className="rounded-lg border bg-card p-4">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-2xl font-bold text-navy">{tile.used}</span>
                  <span className="text-sm text-muted-foreground">/ {tile.limit}</span>
                </div>
                <div className="text-xs text-muted-foreground mb-2">{tile.label}</div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full ${barColor(tile.used, tile.limit)} transition-all`}
                    style={{ width: `${pct(tile.used, tile.limit)}%` }}
                  />
                </div>
                {atLimit && (
                  <a className="text-xs font-bold text-gold hover:underline mt-1.5 inline-block">
                    Upgrade to Add More
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {anyAtLimit && (
        <UpgradePrompt
          feature="Adding more team members"
          description="Upgrade to Agency Pro for higher manager and staff seat limits."
        />
      )}

      {/* Members table */}
      <div className="rounded-xl bg-card border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b">
          <h3 className="font-bold text-navy">Team Members</h3>
        </div>
        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-gold border-t-transparent" />
          </div>
        ) : members.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">No team members yet. Invite your first one above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Role</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium text-muted-foreground hidden md:table-cell">Last Login</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => {
                  const initials = (m.owner_name ?? "U").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
                  const avatarBg = m.team_role === "manager" ? "bg-primary text-primary-foreground" : m.team_role === "owner" ? "bg-gold text-navy" : "bg-muted-foreground text-card";
                  const isCurrentUser = m.id === user?.id;
                  const last = isCurrentUser && !m.last_login
                    ? { text: "today", stale: false }
                    : relativeTime(m.last_login);
                  return (
                    <tr key={m.id} className="border-b hover:bg-muted/20">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`h-8 w-8 rounded-full ${avatarBg} flex items-center justify-center text-xs font-bold`}>
                            {initials}
                          </div>
                          <div>
                            <div className="font-bold text-navy">{m.owner_name ?? "Unnamed"}</div>
                            <div className="text-xs text-muted-foreground">{m.agency_email ?? ""}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {m.team_role === "owner" ? (
                          <Badge className="bg-gold text-navy">Owner</Badge>
                        ) : (
                          <select
                            value={m.team_role}
                            onChange={e => {
                              const newRole = e.target.value as "manager" | "staff";
                              if (newRole === m.team_role) return;
                              setRoleChangeTarget({ member: m, newRole });
                            }}
                            className="text-xs border rounded px-2 py-1 bg-card"
                          >
                            <option value="manager">Manager</option>
                            <option value="staff">Staff</option>
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!m.invite_accepted ? (
                          <Badge variant="outline" className="text-warning border-warning">Pending</Badge>
                        ) : last.stale ? (
                          <Badge variant="outline" className="text-hot border-hot">Inactive</Badge>
                        ) : (
                          <Badge className="bg-success/15 text-success border-success/30">Active</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className={last.stale ? "text-hot text-xs font-medium" : "text-xs text-muted-foreground"}>
                          {last.text}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button title="View activity" className="p-1.5 rounded hover:bg-muted text-muted-foreground" onClick={() => { loadLeads(); setActivityTarget(m); }}>
                            <Eye className="h-4 w-4" />
                          </button>
                          {m.team_role !== "owner" && (
                            <>
                              <button title="Assign leads" className="p-1.5 rounded hover:bg-muted text-muted-foreground" onClick={() => setAssignTarget(m)}>
                                <UserPlus className="h-4 w-4" />
                              </button>
                              <button
                                title={`Reassign ${leadCountFor(m.id)} lead(s)`}
                                disabled={leadCountFor(m.id) === 0}
                                className="p-1.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                                onClick={() => setReassignTarget(m)}
                              >
                                <RefreshCw className="h-4 w-4" />
                              </button>
                              <button title="Remove" className="p-1.5 rounded hover:bg-hot/10 text-hot" onClick={() => removeMember(m)}>
                                <UserX className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InviteTeamModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        managerSeats={{ used: counts.manager, limit: seatLimits.manager }}
        staffSeats={{ used: counts.staff, limit: seatLimits.staff }}
        agencyId={agencyId}
        onInvited={loadMembers}
      />

      {assignTarget && (
        <LeadAssignmentModal
          open={!!assignTarget}
          onOpenChange={(o) => { if (!o) setAssignTarget(null); }}
          staffId={assignTarget.id}
          staffName={assignTarget.owner_name ?? "team member"}
          leads={unassignedLeads}
          onAssigned={() => { loadLeads(); }}
        />
      )}

      {reassignTarget && (
        <BulkReassignModal
          open={!!reassignTarget}
          onOpenChange={(o) => { if (!o) setReassignTarget(null); }}
          fromUserId={reassignTarget.id}
          fromName={reassignTarget.owner_name ?? "team member"}
          leadIds={leadsForReassign}
          members={members as TeamMember[]}
          onReassigned={() => { loadLeads(); loadMembers(); }}
        />
      )}

      {activityTarget && (
        <StaffActivityModal
          open={!!activityTarget}
          onOpenChange={(o) => { if (!o) setActivityTarget(null); }}
          staffId={activityTarget.id}
          staffName={activityTarget.owner_name ?? "Unnamed"}
          staffRole={activityTarget.team_role}
          leads={allLeads}
        />
      )}

      <AlertDialog open={!!roleChangeTarget} onOpenChange={(o) => { if (!o) setRoleChangeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change role to {roleChangeTarget?.newRole}?</AlertDialogTitle>
            <AlertDialogDescription>
              This updates {roleChangeTarget?.member.owner_name ?? "this member"}'s access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmChangeRole}>Change Role</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.owner_name ?? "this member"} from the team?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll lose access to this agency immediately. This can't be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveMember}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
